import { PageHeaderLink } from "@/components/PageHeaderLink";
import { PoweredBy } from "@/components/PoweredBy";
import { StatusBadge } from "@/components/StatusBadge";
import { StatusHistorySegments } from "@/components/StatusHistorySegments";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Item,
	ItemContent,
	ItemDescription,
	ItemTitle,
} from "@/components/ui/item";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatRelativeTime } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

interface Payload {
	application: { id: string; name: string | null };
	service: {
		id: string;
		name: string;
		lastStatus: string | null;
		lastCheckedAt: string | null;
		history: {
			date: string;
			downtime: number | null;
			availability: number | null;
		}[];
		incidents?: {
			startedAt: string;
			endedAt: string | null;
			durationMinutes: number | null;
			message: string | null;
			status: string | null;
		}[];
	};
	days: number;
}

async function fetchServiceStatus(
	appId: string,
	serviceId: string,
): Promise<Payload> {
	const res = await fetch(
		`/api/applications/${appId}/services/${serviceId}/status?days=90`,
		{ credentials: "include" },
	);
	if (res.status === 404) throw new Error("Not found");
	if (res.status === 403) throw new Error("Not public");
	if (!res.ok) throw new Error("Failed to load");
	return res.json() as Promise<Payload>;
}

export function PublicServiceStatus() {
	const { appId, serviceId } = useParams<{
		appId: string;
		serviceId: string;
	}>();

	const { data, isPending, error } = useQuery({
		queryKey: ["public-service-status", appId, serviceId],
		queryFn: () => fetchServiceStatus(appId ?? "", serviceId ?? ""),
		enabled: !!appId && !!serviceId,
	});

	if (error)
		return (
			<div className="container max-w-3xl mx-auto p-6 text-center text-destructive">
				{error.message}
			</div>
		);
	if (isPending || !data) {
		return (
			<div className="container max-w-3xl mx-auto p-6">
				<Skeleton className="h-5 w-40 mb-4" />
				<Skeleton className="h-8 w-48 mb-6" />
				<Skeleton className="h-20 w-full mb-6" />
				<Skeleton className="h-32 w-full" />
			</div>
		);
	}

	const s = data.service;

	const availabilityValues = s.history
		.map((h) => h.availability)
		.filter((a): a is number => a != null);
	const avgAvailability =
		availabilityValues.length > 0
			? availabilityValues.reduce((s, a) => s + a, 0) /
				availabilityValues.length
			: null;

	return (
		<div className="container max-w-3xl mx-auto p-6">
			<div className="mb-4">
				<PageHeaderLink
					to={`/public/${appId}`}
					label={data.application.name ?? "Status"}
					className="text-sm"
				/>
			</div>
			<div className="mb-6">
				<h1 className="text-2xl font-bold tracking-tight text-balance">{s.name}</h1>
			</div>
			<Item variant="outline" className="mb-6">
				<ItemContent>
					<ItemTitle className="flex flex-wrap items-center gap-2">
						Status
						<StatusBadge status={s.lastStatus} />
						{avgAvailability != null && (
							<span
								className={cn(
									"inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium tabular-nums",
									avgAvailability >= 0.99 &&
										"bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
									avgAvailability >= 0.95 &&
										avgAvailability < 0.99 &&
										"bg-lime-500/15 text-lime-700 dark:text-lime-400",
									avgAvailability >= 0.9 &&
										avgAvailability < 0.95 &&
										"bg-amber-500/15 text-amber-700 dark:text-amber-400",
									avgAvailability < 0.9 && "bg-destructive/15 text-destructive",
								)}
							>
								{avgAvailability < 1
									? (avgAvailability * 100).toFixed(1)
									: "100"}
								% uptime
							</span>
						)}
					</ItemTitle>
					{s.lastCheckedAt && (
						<ItemDescription>
							Updated {formatRelativeTime(s.lastCheckedAt)}
						</ItemDescription>
					)}
				</ItemContent>
			</Item>
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">History</CardTitle>
				</CardHeader>
				<CardContent>
					<StatusHistorySegments history={s.history} incidents={s.incidents} />
				</CardContent>
			</Card>
			<div className="mt-6 text-center">
				<PoweredBy />
			</div>
		</div>
	);
}
